/**
 * Mapeo de storefront a monedas aceptadas.
 * Algunos storefronts LATAM aceptan tanto USD como su moneda local,
 * ya que RAM puede devolver PNRs en cualquiera de las dos.
 */
export const STOREFRONT_CURRENCY_MAP = {
  'en-ca': ['CAD'],
  'en-eu': ['EUR'],
  'en-gb': ['GBP'],
  'en-us': ['USD'],
  'es-cl': ['USD', 'CLP'],
  'es-co': ['COP', 'USD'],
  'es-cr': ['USD', 'CRC'],
  'es-do': ['USD', 'DOP'],
  'es-ec': ['USD'],
  'es-es': ['EUR'],
  'es-gt': ['USD', 'GTQ'],
  'es-hn': ['USD', 'HNL'],
  'es-mx': ['MXN'],
  'es-ni': ['USD', 'NIO'],
  'es-pa': ['USD'],
  'es-pe': ['USD', 'PEN'],
  'es-sv': ['USD'],
  'es-us': ['USD'],
  'fr-ca': ['CAD'],
  'fr-fr': ['EUR'],
  'pt-br': ['BRL'],
};

/**
 * Extrae subCodes únicos de un array de quotations.
 * Excluye '00', vacíos, null y undefined.
 * Aplica null-safety con optional chaining.
 *
 * @param { Array } quotations - Array de quotations
 * @returns { string[] } - Array de subCodes únicos
 */
export const getUniqueSubCodes = (quotations) => {
  const seen = new Set();
  const result = [];

  (quotations || []).forEach((item) => {
    const subCode = item?.quoteItemResult?.subCode?.trim() || '';
    if (subCode !== '' && subCode !== '00' && !seen.has(subCode)) {
      seen.add(subCode);
      result.push(subCode);
    }
  });

  return result;
};

/**
 * Agrega resultados de Promise.allSettled en un array unificado.
 * Para llamadas fallidas, genera un objeto con treatment code de error.
 *
 * @param { Array } results - Resultados de Promise.allSettled
 * @param { string[] } subCodes - SubCodes correspondientes a cada promise
 * @returns { Array } - Array unificado de códigos de tratamiento
 */
export const aggregateResults = (results, subCodes) => {
  const aggregated = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled' && !result.value?.error) {
      const responseArray = Array.isArray(result.value) ? result.value : [result.value];
      aggregated.push(...responseArray);
    } else {
      aggregated.push({
        description: '',
        processing: 'F',
        reason: '',
        specialButton: 'False',
        subcode: subCodes[index],
      });
    }
  });

  return aggregated;
};

/**
 * Valida que la moneda del PNR coincida con la moneda esperada del storefront.
 *
 * @param { string } pnrCurrency - Moneda del PNR
 * @param { string } storefront - Storefront actual
 * @returns { { isValid: boolean, expectedCurrency: string|null, correctStorefront: string|null } }
 */
export const validateCurrencyStorefront = (pnrCurrency, storefront) => {
  const acceptedCurrencies = STOREFRONT_CURRENCY_MAP[storefront] || null;
  const isValid = !acceptedCurrencies || acceptedCurrencies.includes(pnrCurrency);

  let correctStorefront = null;
  if (!isValid) {
    correctStorefront = Object.entries(STOREFRONT_CURRENCY_MAP)
      .find(([, currencies]) => currencies.includes(pnrCurrency))?.[0] || null;
  }

  return { correctStorefront, expectedCurrency: acceptedCurrencies?.[0] || null, isValid };
};
